// @ts-nocheck
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { usePersistedState } from '@/lib/filters/persisted-state';
import { Play, Square, Trash2 } from 'lucide-react';

import { getApiCall } from '@/service/httpCall';
import { API_RECORDINGS_LIST } from '@/service/url';
import { USER_ROLES } from '@/utilities/constant';
import { getUser } from '@/utilities/setting';
import { storageUrlOr } from '@/utilities/storageUrl';
import { isMediaReady, MEDIA_NOT_READY_TITLE } from '@/utilities/cdrMedia';
import { canReadRecordings } from '@/utilities/recordingsAcl';
import { fetchMediaAudioUrl, revokeMediaAudioUrl } from '@/utilities/mediaAudio';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import DeleteMediaDialog from './DeleteMediaDialog';
import { DateRangeFilter } from '@/components/filters/DateRangeFilter';

const MAX_RANGE_MONTHS = 3;
const PAGE_SIZE = 50;

const toDateInput = (date) => {
  const off = date.getTimezoneOffset();
  return new Date(date.getTime() - off * 60000).toISOString().slice(0, 10);
};

const defaultRange = () => {
  const now = new Date();
  return { from: toDateInput(new Date(now.getFullYear(), now.getMonth(), 1)), to: toDateInput(now) };
};

const minFromDate = () => {
  const d = new Date();
  d.setMonth(d.getMonth() - MAX_RANGE_MONTHS);
  return toDateInput(d);
};

const withinLimit = (from, to) => {
  if (!from || !to) return true;
  const start = new Date(`${from}T00:00:00`);
  const max = new Date(start);
  max.setMonth(max.getMonth() + MAX_RANGE_MONTHS);
  return new Date(`${to}T00:00:00`).getTime() <= max.getTime();
};

const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const power = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const scaled = value / 1024 ** power;
  return `${scaled >= 10 || power === 0 ? Math.round(scaled) : scaled.toFixed(1)} ${units[power]}`;
};

const fmtDateTime = (value) =>
  value
    ? new Date(value).toLocaleString('en-IN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    : '—';

export default function RecordingsPanel({ onChanged }) {
  const DEFAULTS = useMemo(defaultRange, []);
  const MIN_FROM = useMemo(minFromDate, []);

  const [fromDate, setFromDate] = usePersistedState('dashboard/RecordingsPanel:fromDate', DEFAULTS.from);
  const [toDate, setToDate] = usePersistedState('dashboard/RecordingsPanel:toDate', DEFAULTS.to);
  const [rows, setRows] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);
  const [playingId, setPlayingId] = useState(null);
  const [playingUrl, setPlayingUrl] = useState(null);
  const [deleteRow, setDeleteRow] = useState(null);
  const audioRef = useRef(null);

  const canDelete = getUser()?.role === USER_ROLES.CALL_CENTRE_ADMIN;

  const validate = useCallback(() => {
    if (!fromDate || !toDate) return toast.error('From date and To date are required'), false;
    if (fromDate < MIN_FROM)
      return toast.error('From date cannot be earlier than the last 3 months'), false;
    if (fromDate > toDate) return toast.error('From date cannot be after To date'), false;
    if (!withinLimit(fromDate, toDate))
      return toast.error('Date range cannot exceed 3 months'), false;
    return true;
  }, [fromDate, toDate, MIN_FROM]);

  const fetchPage = useCallback(
    async ({ append = false, useCursor = null } = {}) => {
      if (!validate()) return;

      const params = { from_date: fromDate, to_date: toDate, limit: PAGE_SIZE };

      if (append && useCursor) {
        params.cursor_at = useCursor.created_at;
        params.cursor_id = useCursor.id;
      }

      setLoading(true);
      try {
        const data = await getApiCall(API_RECORDINGS_LIST, params);
        const next = Array.isArray(data?.rows) ? data.rows : [];

        setRows((prev) => (append ? [...prev, ...next] : next));
        setHasMore(Boolean(data?.has_more));
        setCursor(data?.next_cursor || null);
      } catch {
        if (!append) {
          setRows([]);
          setHasMore(false);
          setCursor(null);
        }
      } finally {
        setLoading(false);
      }
    },
    [validate, fromDate, toDate],
  );

  useEffect(() => {
    fetchPage();
  }, []);

  useEffect(() => () => revokeMediaAudioUrl(playingUrl), [playingUrl]);

  useEffect(
    () => () => {
      if (audioRef.current) {
        audioRef.current.pause();
        audioRef.current = null;
      }
    },
    [],
  );

  const stopPlayback = useCallback(() => {
    if (audioRef.current) audioRef.current.pause();
    setPlayingId(null);
    setPlayingUrl(null);
  }, []);

  const startPlayback = useCallback(async (row) => {
    if (!isMediaReady(row) || !canReadRecordings()) return;
    try {
      const objectUrl = await fetchMediaAudioUrl(
        storageUrlOr('recordings', row?.blob_path, ''),
      );
      setPlayingUrl(objectUrl);
      setPlayingId(row?.id);
    } catch (err) {
      toast.error(err?.message || 'Unable to play this recording');
    }
  }, []);

  const columns = useMemo(() => {
    const base = [
      { id: 'created_at', header: 'Recorded', cell: ({ row }) => fmtDateTime(row.original.created_at) },
      { id: 'agent_name', header: 'Agent', cell: ({ row }) => row.original.agent_name || '—' },
      { id: 'agent_group_name', header: 'Group', cell: ({ row }) => row.original.agent_group_name || '—' },
      { id: 'did_number', header: 'DID', cell: ({ row }) => row.original.did_number || row.original.did || '—' },
      { accessorKey: 'size', header: 'Size', cell: ({ row }) => formatBytes(row.original.size) },
      {
        id: 'listen',
        header: 'Listen',
        enableSorting: false,
        cell: ({ row }) => {
          const isPlaying = playingId === row.original.id;
          const ready = isMediaReady(row.original);
          return (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              disabled={!ready}
              onClick={() => (isPlaying ? stopPlayback() : startPlayback(row.original))}
              title={!ready ? MEDIA_NOT_READY_TITLE : isPlaying ? 'Stop' : 'Play'}
              aria-label={isPlaying ? 'Stop recording' : 'Play recording'}
            >
              {isPlaying ? <Square className="h-4 w-4" /> : <Play className="h-4 w-4" />}
            </Button>
          );
        },
      },
    ];

    // Only the call centre admin may delete; the API refuses everyone else too.
    if (canDelete) {
      base.push({
        id: 'delete',
        header: '',
        enableSorting: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => setDeleteRow(row.original)}
            title="Delete recording"
            aria-label="Delete recording"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ),
      });
    }

    return base;
  }, [playingId, startPlayback, stopPlayback, canDelete]);

  if (!canReadRecordings()) {
    return (
      <Card className="p-4 text-center text-sm text-muted-foreground">
        You don't have access to recordings.
      </Card>
    );
  }

  return (
    <Card className="p-4">
      <DateRangeFilter
        compact
        className="mb-3"
        fromLabel="From date"
        toLabel="To date"
        value={{ from: fromDate, to: toDate }}
        min={MIN_FROM}
        onChange={({ from, to }) => { setFromDate(from); setToDate(to); }}
        onApply={() => fetchPage()}
        applyLabel="Apply"
        disabled={loading}
      />

      <DataTable columns={columns} data={rows} loading={loading} />

      <div className="mt-3 flex items-center justify-between">
        <span className="text-xs text-muted-foreground">
          {rows.length} loaded{hasMore ? ' · more available' : ''}
        </span>
        {hasMore && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => fetchPage({ append: true, useCursor: cursor })}
            disabled={loading}
          >
            Load more
          </Button>
        )}
      </div>

      {playingUrl && (
        <audio ref={audioRef} src={playingUrl} autoPlay onEnded={stopPlayback} className="mt-3 w-full" controls />
      )}

      <DeleteMediaDialog
        open={Boolean(deleteRow)}
        onOpenChange={(next) => !next && setDeleteRow(null)}
        type="recordings"
        row={deleteRow}
        onDeleted={(id) => {
          setRows((prev) => prev.filter((r) => r.id !== id));
          onChanged?.();
        }}
      />
    </Card>
  );
}
